import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { ErrorResponse } from '@chiroup/core/types/ErrorResponse.type';
import { createContext, ReactNode, useState } from 'react';

type OnboardingDraftContextValue = {
  drafts: Record<string, string>;
  change: (key: string, text: string) => void;
  clearSubmitted: (key: string, submitted: string) => void;
};

export const OnboardingDraftContext = createContext<OnboardingDraftContextValue | null>(null);

/** Drafts remain only inside the clinic/user-keyed view, without storage or network writes. */
export const OnboardingDraftProvider = ({ children }: { children: ReactNode }) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  return (
    <OnboardingDraftContext.Provider
      value={{
        drafts,
        change: (key, text) => setDrafts(current => ({ ...current, [key]: text })),
        clearSubmitted: (key, submitted) =>
          setDrafts(current => {
            if (current[key] !== submitted) return current;
            const { [key]: _submitted, ...remaining } = current;
            return remaining;
          }),
      }}
    >
      {children}
    </OnboardingDraftContext.Provider>
  );
};

export const panelClass =
  'overflow-hidden rounded-2xl border border-border bg-card text-card-foreground';
export const inputClass =
  'w-full rounded-xl border border-border bg-background px-3 py-2.5 text-base text-foreground hover:border-primary-300 focus:border-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600/20 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:border-primary-800 dark:focus:border-primary-400 sm:py-2 sm:text-sm';
export const buttonClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-2 text-base font-medium text-white hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 disabled:cursor-not-allowed disabled:opacity-50 motion-safe:transition-transform motion-safe:active:translate-y-px dark:bg-primary-400 dark:text-neutral-950 dark:hover:bg-primary-300 dark:focus-visible:outline-primary-400 sm:min-h-10 sm:text-sm';
export const secondaryClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-base font-medium text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 focus-within:outline focus-within:outline-2 focus-within:outline-primary-700 disabled:cursor-not-allowed disabled:opacity-50 has-disabled:cursor-not-allowed has-disabled:opacity-50 motion-safe:transition-transform motion-safe:active:translate-y-px dark:focus-visible:outline-primary-400 dark:focus-within:outline-primary-400 sm:min-h-10 sm:text-sm';

export const statusLabel = (status: string): string =>
  ({
    not_started: 'Not Started',
    in_progress: 'In Progress',
    done: 'Done',
    ready: 'Ready',
    not_applicable: 'Not Applicable',
    pending_review: 'Pending Review',
    accepted: 'Accepted',
    needs_correction: 'Needs Correction',
    open: 'Open',
    resolved: 'Resolved',
  })[status] || status;

export const Status = ({ value }: { value: string }) => (
  <div
    className={`inline-flex items-center gap-1.5 rounded-full border py-1 pl-2 pr-3 text-base font-medium whitespace-nowrap sm:text-sm ${
      ['done', 'accepted', 'ready', 'resolved'].includes(value)
        ? 'border-emerald-600/15 bg-emerald-50 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-900/25 dark:text-emerald-300'
        : ['needs_correction', 'open'].includes(value)
          ? 'border-amber-600/15 bg-amber-50 text-amber-900 dark:border-amber-400/20 dark:bg-amber-900/25 dark:text-amber-200'
          : ['in_progress', 'pending_review'].includes(value)
            ? 'border-sky-600/15 bg-sky-50 text-sky-800 dark:border-sky-400/20 dark:bg-sky-900/25 dark:text-sky-200'
            : 'border-border bg-muted/60 text-muted-foreground'
    }`}
  >
    <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-70" />
    {statusLabel(value)}
  </div>
);

export const Section = ({
  title,
  description,
  children,
  action,
  tone = 'green',
  eyebrow,
  icon,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
  tone?: 'green' | 'sky' | 'violet' | 'neutral';
  eyebrow?: string;
  icon?: ReactNode;
}) => (
  <section className={panelClass}>
    <div
      className={`flex flex-wrap items-start justify-between gap-4 border-b border-border p-5 sm:px-6 ${tone === 'sky' ? 'bg-sky-50/60 dark:bg-sky-950/20' : tone === 'violet' ? 'bg-violet-50/60 dark:bg-violet-950/20' : tone === 'green' ? 'bg-primary-50/70 dark:bg-primary-900/15' : 'bg-muted/25'}`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {icon && (
          <div
            aria-hidden="true"
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone === 'sky' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/35 dark:text-sky-300' : tone === 'violet' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/35 dark:text-violet-300' : tone === 'green' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-muted text-muted-foreground'}`}
          >
            {icon}
          </div>
        )}
        <div className="space-y-1">
          {eyebrow && (
            <p
              className={`text-base font-medium sm:text-sm ${tone === 'sky' ? 'text-sky-700 dark:text-sky-300' : tone === 'violet' ? 'text-violet-700 dark:text-violet-300' : 'text-primary-800 dark:text-primary-300'}`}
            >
              {eyebrow}
            </p>
          )}
          <h2 className="text-balance text-lg font-semibold">{title}</h2>
          {description && (
            <p className="max-w-[80ch] text-pretty text-base text-muted-foreground sm:text-sm">
              {description}
            </p>
          )}
        </div>
      </div>
      {action}
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

export const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="flex min-w-0 flex-col gap-1.5 text-base font-medium sm:text-sm">
    {label}
    {children}
  </label>
);

export const errorMessage = (error: unknown): string => {
  const details = (error as ErrorResponse | undefined)?.response?.data;
  return (
    details?.message ||
    details?.form?.[0]?.message ||
    (error instanceof Error
      ? error.message
      : 'The request could not be completed. Please try again.')
  );
};

export const instantLabel = (value?: string): string =>
  value
    ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : 'Not recorded';

export const calendarLabel = (value: string | null): string =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeZone: ChiroUpDayJsCommon.defaultTimezone,
      }).format(new Date(`${value}T12:00:00-06:00`))
    : 'To Be Determined';
